// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { UserDocumentMetadata } from '@shared/data-access/types/engine-types';
import { type DocumentScope } from '@shared/data-access/types/engine-types';
import { isDefined } from '@shared/utils';

export interface UploadDocumentMetadataFormValues {
  readonly scope: DocumentScope;
  readonly category: string;
  readonly subcategory: string;
  readonly title: string;
  readonly issuer?: string;
  readonly amount?: string;
  readonly currency?: string;
  readonly document_date?: string;
  readonly expiry_date?: string;
  readonly language?: string;
  readonly description?: string;
}

export interface BuildDocumentMetadataOptions {
  readonly clientId?: number | null;
}

const toOptionalApiString = (value?: string): string | undefined => {
  const trimmed = value?.trim();

  return trimmed ? trimmed : undefined;
};

const toOptionalApiDate = (value?: string): string | undefined => {
  const trimmed = value?.trim();

  if (!trimmed) {
    return undefined;
  }

  return trimmed;
};

export const buildDocumentMetadataFromForm = (
  values: UploadDocumentMetadataFormValues,
  options: BuildDocumentMetadataOptions = {},
): UserDocumentMetadata => {
  const metadata: UserDocumentMetadata = {
    scope: values.scope,
    category: values.category.trim(),
    subcategory: values.subcategory.trim(),
    document_type: '',
    title: values.title.trim(),
  };

  const issuer = toOptionalApiString(values.issuer);
  const language = toOptionalApiString(values.language);
  const description = toOptionalApiString(values.description);
  const documentDate = toOptionalApiDate(values.document_date);
  const expiryDate = toOptionalApiDate(values.expiry_date);
  const amount = toOptionalApiString(values.amount);
  const currency = toOptionalApiString(values.currency);

  if (issuer) {
    metadata.issuer = issuer;
  }

  if (language) {
    metadata.language = language;
  }

  if (description) {
    metadata.description = description;
  }

  if (documentDate) {
    metadata.document_date = documentDate;
  }

  if (expiryDate) {
    metadata.expiry_date = expiryDate;
  }

  if (amount) {
    metadata.amount = amount;
  }

  if (currency) {
    metadata.currency = currency.toLowerCase();
  }

  if (isDefined(options.clientId)) {
    metadata.client_id = options.clientId;
  }

  return metadata;
};
